import React, { useEffect, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { useFieldError } from "@/state/ValidationContext";
import { QUIZ } from "@/theme/quizTokens";

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-8px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(6px); }
`;

type Props = {
  title: string;
  /** Supporting copy rendered below the field (matches the Figma layout). */
  subtitle?: string;
  /** Render the subtitle between the heading and the field instead (treats/allergies screens). */
  subtitleAbove?: boolean;
  illustration?: string;
  field?: string;
  children: React.ReactNode;
};

/**
 * QuestionCard — one focused question on the cream canvas.
 * Figma layout: RL Aqva uppercase heading → field → helper text (below).
 * No card/elevation; the question sits directly on the shell background.
 */
export function QuestionCard({ title, subtitle, subtitleAbove, illustration, field, children }: Props) {
  const fieldError = useFieldError(field ?? "");
  const hasError = field ? fieldError.hasError : false;
  const shakeKey = field ? fieldError.shakeKey : 0;
  const [isShaking, setIsShaking] = useState(false);

  useEffect(() => {
    if (hasError && shakeKey > 0) {
      setIsShaking(true);
      const timer = setTimeout(() => setIsShaking(false), 400);
      return () => clearTimeout(timer);
    }
  }, [hasError, shakeKey]);

  return (
    <Box
      data-field={field}
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: { xs: "20px", md: "28px" },
        py: { xs: "10px", md: 0 },
        borderRadius: "16px",
        border: hasError ? `2px solid ${QUIZ.color.error}` : "2px solid transparent",
        backgroundColor: hasError ? "rgba(198, 40, 40, 0.04)" : "transparent",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        animation: isShaking ? `${shake} 0.4s ease-in-out` : "none",
      }}
    >
      {illustration && (
        <Box
          component="img"
          src={illustration}
          alt=""
          sx={{ maxWidth: { xs: 160, md: 220 }, height: "auto", objectFit: "contain" }}
        />
      )}

      {/* Heading — on desktop it may run wider than the field column (Figma lets it overflow) */}
      <Typography
        component="h1"
        sx={{
          width: { xs: "100%", md: "calc(100% + 64px)" },
          mx: { xs: 0, md: "-32px" },
          fontFamily: QUIZ.font.display,
          fontWeight: 900,
          textTransform: "uppercase",
          color: QUIZ.color.ink,
          fontSize: { xs: "20px", md: "41px" },
          lineHeight: { xs: 1.2, md: 1 },
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>

      {/* Helper copy (above the field — treats/allergies screens) */}
      {subtitle && subtitleAbove && (
        <Typography
          sx={{
            width: "100%",
            fontFamily: QUIZ.font.body,
            fontSize: { xs: "16px", md: "18px" },
            lineHeight: 1.3,
            color: QUIZ.color.ink,
            maxWidth: 500,
            mt: { xs: "-8px", md: "-12px" },
          }}
        >
          {subtitle}
        </Typography>
      )}

      {/* Field */}
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: 500 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {children}
      </Box>

      {/* Helper copy (below the field) */}
      {subtitle && !subtitleAbove && (
        <Typography
          sx={{
            width: "100%",
            fontFamily: QUIZ.font.body,
            fontSize: { xs: "16px", md: "18px" },
            lineHeight: 1.3,
            color: QUIZ.color.grey,
            maxWidth: 500,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}
